import React, { useMemo, useCallback, useState, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  CircleMarker,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import {
  Button,
  Loading,
  Tag,
  TextInput,
  Checkbox,
  DatePicker,
  DatePickerInput,
  TimePicker,
} from '@carbon/react';
import { Renew, ChevronUp, ChevronDown } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useAppData } from '../context/AppDataContext';
import { useFleetMap } from '../hooks/useFleetMap';
import { useFleetTripRoutes } from '../hooks/useFleetTripRoutes';
import SubscriptionGate from '../components/SubscriptionGate';
import { useSubscription } from '../hooks/useSubscription';
import SensorMapPopup from '../components/maps/SensorMapPopup';
import 'leaflet/dist/leaflet.css';
import './MapsPage.scss';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const MARKER_COLORS = {
  zone: '#6929c4',
  sensor: '#198038',
  sensorLive: '#24a148',
  gateway: '#0f62fe',
};

function createMarkerIcon(color, live = false) {
  const size = live ? 34 : 28;
  return L.divIcon({
    className: 'fleet-map-marker',
    html: `<div style="
      background-color:${color};
      width:${size}px;height:${size}px;
      border-radius:50%;
      border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.35);
      ${live ? 'animation:pulse 1.5s infinite;' : ''}
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const FitBounds = ({ markers }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!markers.length) return;
    const bounds = L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds.pad(0.15), { maxZoom: 14 });
  }, [markers, map]);
  return null;
};

const FocusSelection = ({ markers, selectedIds }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!selectedIds?.size) return;
    const selected = markers.filter((m) => selectedIds.has(m.id));
    if (selected.length === 0) return;
    const bounds = L.latLngBounds(selected.map((m) => [m.lat, m.lng]));
    map.fitBounds(bounds.pad(0.25), { maxZoom: 15 });
  }, [markers, selectedIds, map]);
  return null;
};

const FocusDatapoint = ({ point }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!point) return;
    map.flyTo([point.lat, point.lng], Math.max(map.getZoom(), 16), {
      duration: 0.7,
    });
  }, [point, map]);
  return null;
};

function combineLocalDateAndTime(date, time) {
  if (!date || !time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  const value = new Date(date);
  value.setHours(hours, minutes, 0, 0);
  return Number.isNaN(value.getTime()) ? null : value;
}

const MapsPage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user, isAuthenticated } = useAuth();
  const { sensors, zones, gateways } = useAppData();
  const { hasActiveSubscription } = useSubscription();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromTime, setFromTime] = useState('00:00');
  const [toTime, setToTime] = useState('23:59');
  const [timeRange, setTimeRange] = useState(null);
  const [timeFilterError, setTimeFilterError] = useState('');
  const [highlightedDatapoint, setHighlightedDatapoint] = useState(null);
  const { mapMarkers, mapCenter, isLoading, refresh, counts } = useFleetMap({
    from: timeRange?.from || null,
    to: timeRange?.to || null,
  });
  const { tripRoutes, refreshRoutes } = useFleetTripRoutes(
    user?.id,
    sensors,
    isAuthenticated,
    { from: timeRange?.from || null, to: timeRange?.to || null },
  );
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [panelCollapsed, setPanelCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => {
      if (e.matches) setPanelCollapsed(true);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const handleRefresh = useCallback(() => {
    refresh();
    refreshRoutes();
  }, [refresh, refreshRoutes]);

  const setSingleSelect = useCallback((id) => {
    setSelectedIds(new Set([id]));
  }, []);

  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const applyTimeFilter = useCallback(() => {
    const fromValue = combineLocalDateAndTime(fromDate, fromTime);
    let toValue = combineLocalDateAndTime(toDate, toTime);

    if (!fromValue || !toValue) {
      setTimeFilterError('Choose both From and To dates and times.');
      return;
    }

    // Default To time is 23:59 — if that lands later today, clamp to now
    // so "today" still works without a false "future" error.
    const now = new Date();
    if (toValue > now) {
      toValue = now;
    }

    if (fromValue >= toValue) {
      setTimeFilterError('From must be earlier than To.');
      return;
    }

    setTimeFilterError('');
    setTimeRange({
      from: fromValue.toISOString(),
      to: toValue.toISOString(),
    });
  }, [fromDate, fromTime, toDate, toTime]);

  const returnToLive = useCallback(() => {
    setTimeRange(null);
    setTimeFilterError('');
    setHighlightedDatapoint(null);
  }, []);

  const bgPanel = isDark ? 'rgba(22,22,22,0.92)' : 'rgba(255,255,255,0.95)';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const tileUrl = isDark
    ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

  const markerIcons = useMemo(() => {
    const cache = {};
    mapMarkers.forEach((m) => {
      const key = `${m.type}-${m.isLive}`;
      if (!cache[key]) {
        const color = m.type === 'zone'
          ? MARKER_COLORS.zone
          : m.type === 'gateway'
            ? MARKER_COLORS.gateway
            : (m.isLive ? MARKER_COLORS.sensorLive : MARKER_COLORS.sensor);
        cache[key] = createMarkerIcon(color, m.isLive);
      }
    });
    return cache;
  }, [mapMarkers]);

  const filteredMarkers = useMemo(() => {
    if (!selectedIds.size) return mapMarkers;
    return mapMarkers.filter((m) => selectedIds.has(m.id));
  }, [mapMarkers, selectedIds]);

  const deviceList = useMemo(() => {
    const q = search.trim().toLowerCase();
    const match = (name) => !q || (name || '').toLowerCase().includes(q);

    const zoneItems = (zones || [])
      .filter((z) => z?.id && z?.name && match(z.name))
      .map((z) => ({ id: `zone-${z.id}`, type: 'zone', name: z.name }));

    const sensorItems = (sensors || [])
      .filter((s) => s?.id && s?.name && match(s.name))
      .map((s) => ({ id: `sensor-${s.id}`, type: 'sensor', name: s.name }));

    const gatewayItems = (gateways || [])
      .filter((g) => g?.id && (g?.name || g?.mac_address) && match(g.name || g.mac_address))
      .map((g) => ({ id: `gateway-${g.id}`, type: 'gateway', name: g.name || g.mac_address }));

    return { zones: zoneItems, sensors: sensorItems, gateways: gatewayItems };
  }, [zones, sensors, gateways, search]);

  const deviceIndex = useMemo(() => {
    const byId = {};
    deviceList.zones.forEach((d) => { byId[d.id] = d; });
    deviceList.sensors.forEach((d) => { byId[d.id] = d; });
    deviceList.gateways.forEach((d) => { byId[d.id] = d; });
    return byId;
  }, [deviceList]);

  const selectedDevices = useMemo(() => {
    const list = [];
    selectedIds.forEach((id) => {
      const d = deviceIndex[id];
      if (d) list.push(d);
    });
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedIds, deviceIndex]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    const all = [...deviceList.sensors, ...deviceList.gateways, ...deviceList.zones];
    return all
      .filter((d) => !selectedIds.has(d.id))
      .slice(0, 12);
  }, [deviceList, search, selectedIds]);

  return (
    <SubscriptionGate
      feature="fleet maps"
      disabled={!hasActiveSubscription}
      showChildrenGreyed
    >
      <div className="maps-page">
        <div
          className={`maps-page__panel${panelCollapsed ? ' maps-page__panel--collapsed' : ''}`}
          style={{ backgroundColor: bgPanel, border: `1px solid ${borderColor}` }}
        >
          <div className="maps-page__panel-header">
            <button
              type="button"
              className="maps-page__panel-toggle"
              onClick={() => setPanelCollapsed((c) => !c)}
              aria-expanded={!panelCollapsed}
              aria-label={panelCollapsed ? 'Expand fleet map panel' : 'Collapse fleet map panel'}
            >
              <h1 style={{ color: textPrimary, fontSize: '1.125rem', margin: 0 }}>Fleet Map</h1>
              {panelCollapsed && (
                <span className="maps-page__panel-summary" style={{ color: textSecondary }}>
                  {counts.zones + counts.sensors + counts.gateways} devices
                  {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                </span>
              )}
              <span className="maps-page__panel-chevron" style={{ color: textSecondary }}>
                {panelCollapsed ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
              </span>
            </button>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Renew}
              iconDescription="Refresh"
              hasIconOnly
              onClick={handleRefresh}
            />
          </div>

          {!panelCollapsed && (
            <div className="maps-page__panel-body">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <Tag type="purple" size="sm">{counts.zones} zones</Tag>
            <Tag type="green" size="sm">{counts.sensors} sensors</Tag>
            <Tag type="blue" size="sm">{counts.gateways} gateways</Tag>
          </div>

          <div className="maps-page__legend" style={{ color: textSecondary }}>
            <div className="maps-page__legend-item">
              <span className="maps-page__dot" style={{ background: MARKER_COLORS.zone }} />
              Zone
            </div>
            <div className="maps-page__legend-item">
              <span className="maps-page__dot" style={{ background: MARKER_COLORS.sensorLive }} />
              Sensor
            </div>
            <div className="maps-page__legend-item">
              <span className="maps-page__dot" style={{ background: MARKER_COLORS.gateway }} />
              Gateway
            </div>
            {tripRoutes.length > 0 && (
              <div className="maps-page__legend-item">
                <span
                  className="maps-page__route-line"
                  style={{ background: MARKER_COLORS.sensorLive }}
                />
                Trip route
              </div>
            )}
          </div>

          <div className="maps-page__time-filter">
            <div className="maps-page__filter-title" style={{ color: textPrimary }}>
              Telemetry date &amp; time
            </div>
            <div className="maps-page__time-filter-fields">
              <div className="maps-page__time-filter-row">
                <DatePicker
                  datePickerType="single"
                  maxDate={new Date()}
                  onChange={(dates) => setFromDate(dates?.[0] || null)}
                >
                  <DatePickerInput
                    id="fleet-map-from-date"
                    labelText="From date"
                    placeholder="mm/dd/yyyy"
                    size="sm"
                  />
                </DatePicker>
                <TimePicker
                  id="fleet-map-from-time"
                  labelText="From time"
                  value={fromTime}
                  onChange={(event) => setFromTime(event.target.value)}
                  placeholder="HH:mm"
                  size="sm"
                />
              </div>
              <div className="maps-page__time-filter-row">
                <DatePicker
                  datePickerType="single"
                  maxDate={new Date()}
                  onChange={(dates) => setToDate(dates?.[0] || null)}
                >
                  <DatePickerInput
                    id="fleet-map-to-date"
                    labelText="To date"
                    placeholder="mm/dd/yyyy"
                    size="sm"
                  />
                </DatePicker>
                <TimePicker
                  id="fleet-map-to-time"
                  labelText="To time"
                  value={toTime}
                  onChange={(event) => setToTime(event.target.value)}
                  placeholder="HH:mm"
                  size="sm"
                />
              </div>
            </div>
            {timeFilterError && (
              <p className="maps-page__filter-error">{timeFilterError}</p>
            )}
            {timeRange && (
              <Tag type="cyan" size="sm" className="maps-page__range-tag">
                Historical telemetry
              </Tag>
            )}
            <div className="maps-page__time-filter-actions">
              <Button kind="primary" size="sm" onClick={applyTimeFilter}>
                Apply filter
              </Button>
              <Button
                kind="tertiary"
                size="sm"
                disabled={!timeRange}
                onClick={returnToLive}
              >
                Back to live
              </Button>
            </div>
          </div>

          <div className="maps-page__device-search">
            <TextInput
              id="fleet-map-device-search"
              labelText=""
              placeholder="Search zones, sensors, gateways…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="sm"
            />

            {selectedDevices.length > 0 && (
              <div className="maps-page__chip-row">
                {selectedDevices.map((d) => (
                  <Tag
                    key={d.id}
                    size="sm"
                    type={d.type === 'zone' ? 'purple' : d.type === 'gateway' ? 'blue' : 'green'}
                    filter
                    onClose={() => toggleSelect(d.id)}
                    title={d.name}
                  >
                    {d.name}
                  </Tag>
                ))}
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="maps-page__chip-row maps-page__chip-row--suggestions">
                {suggestions.map((d) => (
                  <Tag
                    key={`suggest-${d.id}`}
                    size="sm"
                    type={d.type === 'zone' ? 'purple' : d.type === 'gateway' ? 'blue' : 'green'}
                    title={`Add ${d.name}`}
                    onClick={() => toggleSelect(d.id)}
                  >
                    + {d.name}
                  </Tag>
                ))}
              </div>
            )}

            <div className="maps-page__device-actions">
              <Button
                kind="tertiary"
                size="sm"
                disabled={!selectedIds.size}
                onClick={clearSelection}
              >
                Clear selection
              </Button>
              <Button
                kind="ghost"
                size="sm"
                disabled={!filteredMarkers.length}
                onClick={() => {
                  // FocusSelection will do the fit; this is a no-op trigger for users who want it.
                  // We can add an explicit fit button later if needed.
                  setSelectedIds((prev) => new Set(prev));
                }}
              >
                Focus
              </Button>
            </div>
          </div>

          <div className="maps-page__device-list" style={{ color: textSecondary }}>
            {(['zones', 'sensors', 'gateways']).map((groupKey) => {
              const items = deviceList[groupKey];
              if (!items?.length) return null;
              const title = groupKey === 'zones' ? 'Zones' : groupKey === 'sensors' ? 'Sensors' : 'Gateways';
              return (
                <div key={groupKey} className="maps-page__device-group">
                  <div className="maps-page__device-group-title" style={{ color: textSecondary }}>
                    {title} ({items.length})
                  </div>
                  {items.map((item) => {
                    const isSelected = selectedIds.has(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`maps-page__device-item${isSelected ? ' maps-page__device-item--selected' : ''}`}
                        onClick={() => setSingleSelect(item.id)}
                        title={item.name}
                      >
                        <Checkbox
                          id={`fleet-map-select-${item.id}`}
                          labelText=""
                          checked={isSelected}
                          onChange={() => toggleSelect(item.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span
                          className="maps-page__dot"
                          style={{
                            background:
                              item.type === 'zone'
                                ? MARKER_COLORS.zone
                                : item.type === 'gateway'
                                  ? MARKER_COLORS.gateway
                                  : MARKER_COLORS.sensorLive,
                          }}
                        />
                        <div className="maps-page__device-item-name" style={{ color: textPrimary }}>
                          {item.name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {!deviceList.zones.length && !deviceList.sensors.length && !deviceList.gateways.length && (
              <p style={{ color: textSecondary, fontSize: '0.8125rem', marginTop: 10, marginBottom: 0 }}>
                No matching devices.
              </p>
            )}
          </div>

          {isLoading && (
            <div style={{ marginTop: 12 }}>
              <Loading small withOverlay={false} description="Loading map data..." />
            </div>
          )}

          {!isLoading && mapMarkers.length === 0 && (
            <p style={{ color: textSecondary, fontSize: '0.8125rem', marginTop: 12, marginBottom: 0 }}>
              No devices on the map yet.
            </p>
          )}
            </div>
          )}
        </div>

        <MapContainer
          center={mapCenter}
          zoom={11}
          className="maps-page__map"
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url={tileUrl}
          />
          {filteredMarkers.length > 1 && <FitBounds markers={filteredMarkers} />}
          <FocusSelection markers={mapMarkers} selectedIds={selectedIds} />
          <FocusDatapoint point={highlightedDatapoint} />
          {tripRoutes.map((route) => (
            <Polyline
              key={`route-${route.id}`}
              positions={route.path}
              pathOptions={{
                color: route.isLive ? MARKER_COLORS.sensorLive : MARKER_COLORS.sensor,
                weight: route.roadMatched ? 5 : 3,
                opacity: route.isLive ? 0.9 : 0.65,
                dashArray: route.roadMatched ? undefined : '6 8',
              }}
            />
          ))}
          {filteredMarkers.map((marker) => {
            const iconKey = `${marker.type}-${marker.isLive}`;
            return (
              <Marker
                key={marker.id}
                position={[marker.lat, marker.lng]}
                icon={markerIcons[iconKey]}
                eventHandlers={{
                  click: (e) => {
                    const isMulti = !!e?.originalEvent?.shiftKey;
                    if (isMulti) toggleSelect(marker.id);
                    else setSingleSelect(marker.id);
                  },
                }}
              >
                <Popup>
                  <SensorMapPopup
                    marker={marker}
                    isDark={isDark}
                    timeRange={timeRange}
                    onSparklinePointSelect={setHighlightedDatapoint}
                  />
                </Popup>
              </Marker>
            );
          })}
          {highlightedDatapoint && (
            <CircleMarker
              center={[highlightedDatapoint.lat, highlightedDatapoint.lng]}
              radius={10}
              pathOptions={{
                color: '#ffffff',
                weight: 3,
                fillColor: '#da1e28',
                fillOpacity: 1,
              }}
            >
              <Tooltip permanent direction="top" offset={[0, -10]}>
                <strong>{highlightedDatapoint.temperature.toFixed(1)}°</strong>
                <br />
                {new Date(highlightedDatapoint.readingAt).toLocaleString()}
              </Tooltip>
            </CircleMarker>
          )}
        </MapContainer>
      </div>
    </SubscriptionGate>
  );
};

export default MapsPage;
